import z from "zod";
import {
  FloorPlanIdScheme,
  PaymentMethodIdScheme,
  StoreFrontIdScheme,
} from "../ids";
import { OpeningHoursScheme } from "../openingHours";
import { PaymentMethodType } from "../enums/PaymentMethodType";
import { OrderWorkflowStageScheme } from "./orderWorkflow";
import { CustomerInfoRequirement } from "../enums/CustomerInfoRequirement";

const StoreFrontPaymentMethodBaseScheme = z.object({
  id: PaymentMethodIdScheme,
  name: z.string(),
  enabled: z.boolean(),
});

export const StoreFrontPaymentMethodSwishCommerceScheme =
  StoreFrontPaymentMethodBaseScheme.extend({
    type: z.literal(PaymentMethodType.SWISH_COMMERCE),
  });

export const StoreFrontPaymentMethodVivaSmartCheckoutScheme =
  StoreFrontPaymentMethodBaseScheme.extend({
    type: z.literal(PaymentMethodType.VIVA_SMART_CHECKOUT),
    sourceCode: z.string(),
  });

export const StoreFrontPaymentMethodGiftCardScheme =
  StoreFrontPaymentMethodBaseScheme.extend({
    type: z.literal(PaymentMethodType.GIFT_CARD),
  });

export const StoreFrontPaymentMethodCreditNoteScheme =
  StoreFrontPaymentMethodBaseScheme.extend({
    type: z.literal(PaymentMethodType.CREDIT_NOTE),
  });

export const StoreFrontPaymentMethodScheme = z.discriminatedUnion("type", [
  StoreFrontPaymentMethodSwishCommerceScheme,
  StoreFrontPaymentMethodVivaSmartCheckoutScheme,
  StoreFrontPaymentMethodGiftCardScheme,
  StoreFrontPaymentMethodCreditNoteScheme,
]);

export const CustomerInfoPolicyScheme = z.object({
  name: z.enum(CustomerInfoRequirement),
  email: z.enum(CustomerInfoRequirement),
  phone: z.enum(CustomerInfoRequirement),
  deliveryAddress: z.enum(CustomerInfoRequirement),
  note: z.enum(CustomerInfoRequirement),
});

const StoreFrontModeTableScheme = z.object({
  enabled: z.boolean(),
  customerInfo: CustomerInfoPolicyScheme,
  orderStages: z.array(OrderWorkflowStageScheme),
  allowTableSelection: z.boolean(),
  selectableFloorPlanIds: z.array(FloorPlanIdScheme),
});

const StoreFrontModeTabScheme = z.object({
  enabled: z.boolean(),
  customerInfo: CustomerInfoPolicyScheme,
  allowItemSplit: z.boolean(),
  allowEvenSplit: z.boolean(),
  allowPartialPayment: z.boolean(),
});

const StoreFrontModeTakeawayScheme = z.object({
  enabled: z.boolean(),
  customerInfo: CustomerInfoPolicyScheme,
  orderStages: z.array(OrderWorkflowStageScheme),
  allowScheduled: z.boolean(),
});

export const StoreFrontModesScheme = z.object({
  table: StoreFrontModeTableScheme.optional(),
  tab: StoreFrontModeTabScheme.optional(),
  takeaway: StoreFrontModeTakeawayScheme.optional(),
});

export const StoreFrontDBScheme = z.object({
  id: StoreFrontIdScheme,

  name: z.string(),
  enabled: z.boolean(),

  heroEnabled: z.boolean(),
  heroTitle: z.string().nullable().optional(),
  heroDescription: z.string().nullable().optional(),
  heroImage: z.string().nullable().optional(),

  openingHours: OpeningHoursScheme.nullable().optional(),

  tippingEnabled: z.boolean(),
  defaultPrepDurationMinutes: z.number().int().nonnegative(),

  modes: StoreFrontModesScheme,

  paymentMethods: z.array(StoreFrontPaymentMethodScheme),

  giftCardsEnabled: z.boolean().optional(),
  giftCardAllowCustomAmount: z.boolean().optional(),
  giftCardPresetValues: z.array(z.number().int().nonnegative()).optional(),
  giftCardMinimumAmount: z.number().int().nonnegative().optional(),
  giftCardMaximumAmount: z.number().int().nonnegative().optional(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const RestGetStoreFront = StoreFrontDBScheme;

export const RestCreateStoreFront = StoreFrontDBScheme.omit({
  id: true,
  updatedAt: true,
  createdAt: true,
});

export const RestUpdateStoreFront = StoreFrontDBScheme.omit({
  id: true,
  updatedAt: true,
  createdAt: true,
}).partial();

export type StoreFront = z.infer<typeof StoreFrontDBScheme>;
export type StoreFrontModes = z.infer<typeof StoreFrontModesScheme>;
export type CustomerInfoPolicy = z.infer<typeof CustomerInfoPolicyScheme>;
export type RestCreateStoreFront = z.infer<typeof RestCreateStoreFront>;
export type RestUpdateStoreFront = z.infer<typeof RestUpdateStoreFront>;
